import React, { useCallback, useMemo, useState } from 'react';
import { Button, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router';
import { useProject, useProjectPhase } from '../hooks';
import { IPhase } from '../types';
import { Loader } from '../../../components/UI/Loader';
import { queries } from '../../../graphql/graphql';
import MapList, { MapListNextPayload } from '../../../pages/Map/MapList';
import { PlanView } from '../../../pages/Plans/View';
import type { MapPhaseData } from '../models/MapPhaseModel';

interface ProjectMapPageProps {
  phaseId?: string;
}

export const ProjectMapPage: React.FC<ProjectMapPageProps> = ({ phaseId }) => {
  const { phaseId: routePhaseId } = useParams();
  const navigate = useNavigate();
  const { project, getNextPhase, persistActivePhase } = useProject();
  const resolvedPhaseId = phaseId || routePhaseId;
  const { phase, savePhase, setData, loading } = useProjectPhase(resolvedPhaseId);
  const [isWorking, setIsWorking] = useState(false);

  const phaseData = useMemo<MapPhaseData>(() => {
    return (phase?.data || {}) as MapPhaseData;
  }, [phase?.data]);

  const stage = useMemo(() => {
    if (phaseData.stage !== 'plan') return 'map';
    if (!phaseData.planId || !phaseData.mapResultId) return 'map';
    return 'plan';
  }, [phaseData.mapResultId, phaseData.planId, phaseData.stage]);

  const runWithLock = useCallback(
    async (action: () => Promise<void>) => {
      if (isWorking) return;
      setIsWorking(true);
      try {
        await action();
      } finally {
        setIsWorking(false);
      }
    },
    [isWorking]
  );

  const saveMapPhase = useCallback(
    async (patch: Partial<MapPhaseData>, phasePatch?: Partial<IPhase>) => {
      if (!phase) return;
      const sanitizedPatch = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined)
      ) as Partial<MapPhaseData>;
      const nextData: MapPhaseData = {
        ...phaseData,
        ...sanitizedPatch,
      };
      await savePhase({ data: nextData, ...(phasePatch || {}) });
    },
    [phase, phaseData, savePhase]
  );

  const completePlanPhase = useCallback(async () => {
    if (!phase) return;
    const nextPhase = getNextPhase(phase.id);
    if (!nextPhase) return;

    try {
      await saveMapPhase(
        { stage: 'plan' },
        { progress: 100, status: 'completed', isActive: false }
      );
      await persistActivePhase(nextPhase.id, { locked: false });
      if (project) {
        navigate(`/project/${project.id}/phase/${nextPhase.id}`);
      }
    } catch (error) {
      message.error('Failed to complete map phase');
      console.error('Map phase completion error', error);
    }
  }, [getNextPhase, navigate, persistActivePhase, project, phase, saveMapPhase]);

  const handleBack = useCallback(async () => {
    await runWithLock(async () => {
      try {
        const nextData: MapPhaseData = { ...phaseData, stage: 'map' };
        setData(nextData);
      } catch (error) {
        message.error('Failed to update phase stage');
        console.error('Map phase back error', error);
      }
    });
  }, [phaseData, runWithLock, savePhase, setData]);

  const handleNext = useCallback(
    async (data?: MapListNextPayload) => {
      await runWithLock(async () => {
        if (stage === 'map') {
          if (!data) return;
          try {
            const nextData: MapPhaseData = {
              ...phaseData,
              address: data.address ?? phaseData.address,
              planId: data.planId ?? phaseData.planId,
              mapResultId: data.mapResultId ?? phaseData.mapResultId,
              coordinates: data.coordinates ?? phaseData.coordinates,
              rotation: data.rotation ?? phaseData.rotation,
              stage: 'plan',
            };
            setData(nextData);
            await savePhase({ data: nextData }, { omitIsActive: true });
          } catch (error) {
            message.error('Failed to save map phase');
            console.error('Map phase save error', error);
          }
          return;
        }

        await completePlanPhase();
      });
    },
    [completePlanPhase, phaseData, runWithLock, savePhase, stage]
  );

  if (loading) return <Loader />;
  if (!phase) return null;

  return (
    <div>
      {stage === 'plan' ? (
        <>
          <Button
            onClick={handleBack}
            icon={<LeftOutlined />}
            style={{
              position: 'fixed',
              top: 86,
              left: 16,
              zIndex: 1100,
              backgroundColor: '#000',
              borderColor: '#000',
              color: '#fff',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2)',
              padding: '0 16px',
            }}>
            Back
          </Button>
          <PlanView
            id={phaseData.planId}
            mapId={phaseData.mapResultId}
            onNext={handleNext}
            hideSimilarHomes
          />
        </>
      ) : (
        <MapList
          key={phase.id}
          queries={queries.plans.PLANS}
          prefix="plan"
          onNext={handleNext}
          initialPhaseData={phaseData}
        />
      )}
    </div>
  );
};
